import { gql } from '@apollo/client';
import { CLASSIFICATION_SELECTION, HOLDING_FACTOR_CONTRIBUTIONS } from './portfolios';
import { REGRESSION_EXPOSURE_FIELDS } from './factors';

/* ── the scheme master ──────────────────────────────────────── */

/**
 * What each holding contributes to the fund's tilt — the row's weight times its
 * own loading, which the backend does the multiplying for.
 *
 * The only per-holding factor record asked for. The raw loadings the type also
 * hangs off each holding went unread once the look-through table switched to
 * showing contributions — a loading reads the same for a small position and a
 * large one, where a contribution reads against the position and has a history
 * behind it to chart. The product comes from the backend rather than being
 * multiplied here because the two records disagree about units: `weight` on
 * this record is a fraction (0.092) while a scheme's `holdings.weight` is a
 * percentage (9.209).
 *
 * Twelve more fields exist on the type and every one of them is null on every
 * row — `momentumScoreContribution`, which follows `momentumScore` being
 * unpublished, and the eleven behavioural traits (rallyHeavy, speculativeMix,
 * tooDefensive, fallingKnives, indexMirror, extremeBets, highChurn,
 * qualityCore, wellBalanced, sizeConcentrated, fadingHoldings). Ask for them
 * again once the engine fills them.
 *
 * `asOfDate` is the same factor run as the loadings, and later than both the
 * fund-level exposures and the holdings disclosure these attach to.
 */

/**
 * The whole scheme master in one call — 1,417 rows.
 *
 * Fetched whole because My Funds filters it in the browser: the backend has no
 * `fundHouse` or `managerName` argument, and paging would mean the dropdowns
 * silently filtered only the page in hand rather than the universe. `managers`
 * arrives populated on every row (565 distinct names), which is what makes the
 * manager filter and its cross-filter against fund house possible without a
 * second request.
 *
 * Growth plans only. The master carries both options of the same scheme —
 * "SBI Silver ETF FoF Regular-Growth" beside its IDCW twin — so the unfiltered
 * 2,743 rows are really 1,417 schemes listed twice, differing in a return that
 * is lower on the IDCW row only because the money was paid out. Filtering at
 * the source rather than in the browser halves the payload and, more
 * importantly, keeps a category's average return from being an average over two
 * ways of holding the same fund. Every row is regular already, so `isRegular`
 * is left unsaid rather than passed as a filter that does nothing.
 *
 * `rankYear1` is the master's own one-year rank within the scheme's peer group.
 * It is not the alpha engine's rank — that comes from `mfAlphaForFund` — and
 * its peer group is narrower than the SEBI `category` on the same row (the
 * 574 index funds carry ranks no higher than 161), so it is shown as a bare
 * position and never as "#12 of <category size>".
 */
export const GET_ALL_MUTUAL_FUNDS = gql`
  query GetAllMutualFunds($limit: Int = 4000, $plan: MutualFundPlanFilter = GROWTH) {
    getAllMutualFunds(limit: $limit, plan: $plan) {
      total
      funds {
        securitySymbol
        schemeName
        fundStandardName
        fundHouse
        category
        nav
        aum
        expenseRatio
        return1yr
        return3yr
        rankYear1
        managers { managerName }
        snapshot {
          categoryReturn1yr
          categoryReturn3yr
        }
      }
    }
  }
`;

/**
 * The same resolver, paged and narrowed — every argument optional, since the
 * backend reads an explicit null as "unfiltered". One document rather than two,
 * because the unfiltered form was the filtered one with its arguments dropped.
 */
export const SEARCH_MUTUAL_FUNDS = gql`
  query SearchMutualFunds($searchQuery: String, $category: String, $assetGroup: String, $limit: Int = 25, $offset: Int = 0) {
    getAllMutualFunds(
      searchQuery: $searchQuery
      category: $category
      assetGroup: $assetGroup
      limit: $limit
      offset: $offset
    ) {
      total
      funds {
        securitySymbol
        fundName
        fundStandardName
        schemeName
        fundHouse
        category
        fundLevelCategoryName
        broadCategoryGroup
        starRating
        benchmarkName
        isIndexFund
        nav
        aum
        expenseRatio
      }
    }
  }
`;

/**
 * The schemes behind a screener result handle (`url_id`).
 *
 * The agent's `screen_mutual_funds` tool stores what it found in Redis under
 * `bolodhan:mf_screener:<id>` for ONE hour (`_SCREENER_TTL = 3600`) and hands
 * out the id. Figures are read fresh by the server, not from the stored blob.
 * An unknown or expired id answers `count: 0`, never an error — and so does a
 * screen-BUILDER handle, which lives in another namespace entirely; that case
 * is what `useScreenerFunds` falls back on `getMyCustomScreens(urlId:)` for.
 */
export const SCREENER_MUTUAL_FUNDS = gql`
  query ScreenerMutualFunds($urlId: String!) {
    getScreenerMutualFunds(urlId: $urlId) {
      urlId
      count
      securities {
        securitySymbol
        schemeName
        fundStandardName
        category
        fundHouse
        nav
        aum
        starRating
        expenseRatio
        alphaPercentile
      }
    }
  }
`;

/**
 * One scheme's whole record, look-through included.
 *
 * `holdings`, `sectorExposures` and `factorExposures` are here rather than
 * coming from `getPortfolio` because a scheme is not a portfolio: that resolver
 * looks up an account row and answers "Portfolio not found" for every one of
 * the 1,417 fund symbols, which is why the deep-dive's look-through panels were
 * blank. `benchmarkFactorExposures` gives the factor bars something to be
 * plotted against instead of a hard-coded zero.
 */
export const GET_FUND = gql`
  query GetFund($symbol: String!) {
    getMetaMutualFund(securitySymbol: $symbol) {
      securitySymbol
      schemeName
      fundName
      fundStandardName
      fundHouse
      isin
      amfiCode
      rtaCode
      category
      fundLevelCategoryName
      broadCategoryGroup
      benchmarkName
      inceptionDate
      investmentObjective
      nav
      aum
      starRating
      expenseRatio
      turnoverRatio
      indianRiskLevel
      exitLoadDetails
      minInitialInvestment
      return1yr
      return3yr
      return5yr
      return10yr
      returnInception
      rankYear1
      rankYear3
      rankYear5
      managers { managerName managerTenure managerStartDate }
      holdings {
        securitySymbol name weight sector marketValue quantity
      }
      holdingFactorContributions { ${HOLDING_FACTOR_CONTRIBUTIONS} }
      sectorExposures { sector weight asOfDate }
      classifications { ${CLASSIFICATION_SELECTION} }
      factorExposures { ${REGRESSION_EXPOSURE_FIELDS} }
      benchmarkFactorExposures { securitySymbol ${REGRESSION_EXPOSURE_FIELDS} }
      snapshot {
        asOfDate nav aum
        return1mth return3mth return6mth returnYtd
        return1yr return3yr return5yr returnInception
        categoryReturn1yr categoryReturn3yr
        rankYear1 rankYear3
        alpha beta sharpeRatio volatility
        # The scheme's own disclosed equity/bond/cash/other split, as percentages
        # summing to 100. The look-through bucketing in getReturnAttribution
        # cannot produce this: cash, repo and government securities fall outside
        # every security group, so its weights stop around 93%. This is the only
        # figure that accounts for the rest.
        assetAllocEquityNet assetAllocBondNet assetAllocCashNet assetAllocOtherNet
      }
    }
  }
`;

/**
 * Scheme names for a list of fund symbols, in one call.
 *
 * The look-through's fund nodes come back unnamed on most portfolios (12 of 13
 * on the one sampled), and a drill that prints "118784.IN" where a fund name
 * belongs is a dump of the row it came from. The scheme master names them.
 */
export const FUNDS_BY_SYMBOLS = gql`
  query FundsBySymbols($securitySymbols: [String!]!) {
    getMutualFundsBySymbols(securitySymbols: $securitySymbols) {
      securitySymbol
      schemeName
    }
  }
`;

/**
 * Names for what the scheme master does not hold: ETFs and model portfolios.
 *
 * A look-through's level-1 holdings are not all schemes — a client portfolio
 * holds model variants and ETFs too — and FUNDS_BY_SYMBOLS answers nothing for
 * those, which left them on screen as symbols. One document, two masters,
 * asked only for the symbols the scheme master left unnamed.
 */
export const ETF_AND_MODEL_NAMES = gql`
  query EtfAndModelNames($securitySymbols: [String!]!) {
    getEtfsBySymbols(securitySymbols: $securitySymbols) {
      securitySymbol
      schemeName
    }
    getPortfoliosBySymbols(securitySymbols: $securitySymbols) {
      securitySymbol
      name
    }
  }
`;
